import * as preact from "preact";

import * as analytics from "analytics";
import * as appStorePurchases from "appStorePurchases";
import * as deviceDetect from "deviceDetect";
import * as experiment from "experiment";
import * as localAPI from "localAPI";
import * as modalController from "modalController";
import stores from "stores/stores";
import { AccountPanel as AccountPanelView } from "views/AccountPanelView";
import * as AccountPanelView2 from "views/AccountPanelView2";
import Dialog from "views/Dialog";

const isBookmarkletUrl = () =>
  /(readlang.com|localhost)\/bookmarklet/.test(window.location.href);

type PremiumUpsellModalOptions = {
  /** Optional only in the iOS app, whose paywall names each plan instead */
  heading?: preact.ComponentChildren;
  subheading?: preact.ComponentChildren;
  onCancel?: () => void;
  plus?: boolean;
  smartDefinitions?: boolean;
  title?: string;
  [key: string]: any;
};

const PremiumUpsellMessage = ({
  heading,
  subheading,
}: {
  heading?: preact.ComponentChildren;
  subheading?: preact.ComponentChildren;
}) =>
  heading || subheading ? (
    <>
      {heading && <h2>{heading}</h2>}
      {subheading && <p>{subheading}</p>}
    </>
  ) : null;

type PremiumUpsellModalContentProps = {
  options: PremiumUpsellModalOptions;
  prettier: boolean;
  /** upsellRabbit1 experiment arm: new-style rabbit and hand-drawn bubble */
  readingRabbit: boolean;
};

class PremiumUpsellModalContent extends preact.Component<
  PremiumUpsellModalContentProps,
  { checkoutLinkBelowFold: boolean }
> {
  state = { checkoutLinkBelowFold: false };
  private checkoutLinkObserver?: IntersectionObserver;

  /**
   * Tracks whether the in-card "See premium plans" link's bottom edge is below
   * the fold, as the user scrolls and the layout settles (e.g. once the rabbit
   * image loads). Only free-path viewers get that link. Preact calls this
   * with null on unmount, which disconnects the observer.
   */
  checkoutLinkRef = (checkoutLink: HTMLAnchorElement | null) => {
    this.checkoutLinkObserver?.disconnect();
    if (!checkoutLink) {
      return;
    }
    this.checkoutLinkObserver = new IntersectionObserver(
      ([entry]) => {
        this.setState({
          checkoutLinkBelowFold:
            entry.boundingClientRect.bottom > window.innerHeight,
        });
      },
      { threshold: [0, 1] }
    );
    this.checkoutLinkObserver.observe(checkoutLink);
  };

  render() {
    const { options, prettier, readingRabbit } = this.props;
    // The arms diverge once the in-card checkout link is first seen below the
    // fold, so both arms are treated there.
    const stickyCheckoutBar =
      this.state.checkoutLinkBelowFold &&
      experiment.getConditionAndTreat("stickyUpsellCta1") === "experiment";

    const { heading, subheading, ...restOptions } = options;
    const panelProps = {
      ...restOptions,
      smartDefinitions: options.smartDefinitions,
    };

    const handleClick = (
      event: preact.JSX.TargetedMouseEvent<HTMLDivElement>
    ) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      if (!target.closest(".continue")) {
        return;
      }

      modalController.cancel();
      options.onCancel?.();
    };

    return (
      <Dialog class="premiumUpsellModal" onClick={handleClick}>
        {prettier ? (
          <>
            <img
              class="closeDialogButton"
              onClick={() => modalController.cancel()}
              src="/content/x-close.svg"
            />
            {(heading || subheading) && (
              <div
                class={`speechBubbleContainer${readingRabbit ? " readingRabbit" : ""}`}
              >
                <img
                  class="rabbitLogo"
                  src={
                    readingRabbit
                      ? "/content/rabbitUpsellReading.webp"
                      : "/content/rabbitPoppingOut.svg"
                  }
                />
                <div class="speechBubble">
                  {!readingRabbit && (
                    <img
                      class="speechBubbleArrow"
                      src="/content/speechBubbleArrow.svg"
                    />
                  )}
                  <PremiumUpsellMessage
                    heading={heading}
                    subheading={subheading}
                  />
                </div>
              </div>
            )}
            <AccountPanelView2.AccountPanel
              {...panelProps}
              plus={options.plus}
              checkoutLinkRef={this.checkoutLinkRef}
            />
            {stickyCheckoutBar && (
              <div class="stickyCheckoutBar">
                <div>
                  <a class="goToCheckout" href="/checkout">
                    See premium plans
                  </a>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <PremiumUpsellMessage heading={heading} subheading={subheading} />
            <AccountPanelView {...panelProps} />
          </>
        )}
      </Dialog>
    );
  }
}

/** A heading's text, for the app's paywall */
const textContent = (children: preact.ComponentChildren) => {
  const element = document.createElement("div");
  preact.render(<>{children}</>, element);
  return element.textContent || undefined;
};

export const show = async function (options: PremiumUpsellModalOptions) {
  analytics.trackEventWithProperties("showUpsellModal", {
    title: options.title,
  });

  // The App Store's plans, as Apple requires, alike everywhere in the app
  // (including its browser, whose toolbar runs the bookmarklet)
  if (deviceDetect.isIOSApp()) {
    const subscribed = await appStorePurchases.showPaywall({
      heading: textContent(options.heading),
      subheading: textContent(options.subheading),
    });
    if (!subscribed) {
      options.onCancel?.();
    }
    return;
  }

  const prettier =
    window.location.pathname !== "/bookmarklet" &&
    !(await localAPI.isLocalMode());

  // Premium viewers are offered a plan change rather than checkout, so only
  // free-path viewers enter the upsellRabbit1 experiment.
  const readingRabbit =
    prettier &&
    !AccountPanelView2.hasPremium(stores.user.state.value) &&
    experiment.getConditionAndTreat("upsellRabbit1") === "experiment";

  if (!isBookmarkletUrl()) {
    // Show smart definition upsell everywhere except the bookmarklet, where it's not available.
    options.smartDefinitions = true;
  }

  modalController.show(
    <PremiumUpsellModalContent
      options={options}
      prettier={prettier}
      readingRabbit={readingRabbit}
    />,
    {
      className: prettier ? "greenBackground" : undefined,
      // Its own layout on phones (and stickyUpsellCta1 depends on it)
      fullScreen: false,
      closeButton: !prettier,
      title: options.title,
      minHeight: 385,
      wide: true,
      cancelCallback: options.onCancel,
    }
  );
};
